import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Target, TrendingUp, TrendingDown, Clock, Plus } from "lucide-react";
import { pct, pts, daysLabel, groupByClient, KIND_LABEL } from "@/lib/accuracy";
import { ProjectDialog } from "@/components/accuracy/AccuracyDialogs";

export const KindBadge = ({ kind, testid }) => (
  <span className={`text-[10px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 border ${kind === "pilot" ? "text-sky-700 bg-sky-50 border-sky-200" : "text-violet-700 bg-violet-50 border-violet-200"}`} data-testid={testid}>{KIND_LABEL[kind]}</span>
);

function ProjectCard({ p }) {
  const up = (p.change || 0) >= 0;
  return (
    <Link to={`/accuracy/${p.id}`} className="block bg-white border border-slate-200 rounded-xl p-4 hover:border-indigo-300 hover:shadow-sm transition-[border-color,box-shadow]" data-testid={`accuracy-card-${p.id}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold text-slate-900 truncate">{p.title}</p>
        <div className="flex gap-1 shrink-0">
          <KindBadge kind={p.kind} testid={`accuracy-kind-${p.id}`} />
          {p.on_target != null && <span className={`text-[10px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 border ${p.on_target ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-200"}`} data-testid={`accuracy-target-badge-${p.id}`}>{p.on_target ? "On target" : "Below target"}</span>}
        </div>
      </div>
      <p className="text-xs text-slate-500 flex items-center gap-1 mt-1" data-testid={`accuracy-days-${p.id}`}><Clock className="w-3 h-3" />{daysLabel(p)} · {p.start_date}{p.end_date ? ` → ${p.end_date}` : ""}</p>
      <div className="flex items-end justify-between mt-4">
        <div><p className="text-[10px] uppercase tracking-wider text-slate-400">Latest</p><p className="font-display text-3xl font-bold text-slate-900" data-testid={`accuracy-latest-${p.id}`}>{pct(p.latest)}</p></div>
        {p.change != null && p.readings_count > 1 && <p className={`text-sm font-semibold flex items-center gap-1 ${up ? "text-emerald-600" : "text-red-600"}`} data-testid={`accuracy-change-${p.id}`}>{up ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}{pts(p.change)}</p>}
      </div>
      <p className="text-[11px] text-slate-400 mt-2">Baseline {pct(p.baseline)}{p.target != null && <> · <Target className="w-3 h-3 inline -mt-0.5" /> {pct(p.target)}</>} · {p.readings_count} reading(s)</p>
    </Link>
  );
}

const FILTERS = [["all", "All"], ["pilot", "Pilot"], ["commercial", "Commercial"]];

export default function AccuracyPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isManager = user?.role === "manager" || user?.role === "admin";
  const [list, setList] = useState(null);
  const [kind, setKind] = useState("all");
  const [creating, setCreating] = useState(false);
  useEffect(() => { api.get("/accuracy").then((r) => setList(r.data)); }, []);
  const shown = (list || []).filter((p) => kind === "all" || p.kind === kind);
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px]" data-testid="accuracy-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Accuracy tracker</h1>
          <p className="text-sm text-slate-500 mt-1">{user?.role === "client" ? "Accuracy of your projects since they started." : "Accuracy improvement per client and project."}</p>
        </div>
        {isManager && <Button onClick={() => setCreating(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="accuracy-new-project-button"><Plus className="w-4 h-4 mr-1.5" />New project</Button>}
      </div>
      <div className="inline-flex bg-slate-100 rounded-lg p-1 mt-5 mb-6" data-testid="accuracy-kind-filter">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setKind(k)} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${kind === k ? "bg-white shadow-sm text-slate-900 font-medium" : "text-slate-500 hover:text-slate-800"}`} data-testid={`accuracy-filter-${k}`}>
            {l} <span className="text-xs text-slate-400">{(list || []).filter((p) => k === "all" || p.kind === k).length}</span>
          </button>
        ))}
      </div>
      {list && !shown.length && <p className="text-sm text-slate-400" data-testid="accuracy-empty">{list.length ? "No projects of this type." : isManager ? "No projects yet. Create one to start tracking accuracy." : "No projects yet."}</p>}
      <div className="space-y-8">
        {groupByClient(shown).map(([key, g]) => (
          <section key={key} data-testid={`accuracy-client-${key}`}>
            <h2 className="text-base md:text-lg font-display font-semibold text-slate-900 mb-3">{g.name} <span className="text-xs font-sans font-normal text-slate-400">{g.projects.length} project(s)</span></h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{g.projects.map((p) => <ProjectCard key={p.id} p={p} />)}</div>
          </section>
        ))}
      </div>
      {creating && <ProjectDialog onClose={() => setCreating(false)} onSaved={(p) => nav(`/accuracy/${p.id}`)} />}
    </div>
  );
}
